/**
 * warningStore - Zustand store for managing visual warning state
 *
 * Provides immediate visual feedback for proctoring warnings without
 * waiting for avatar speech queue. Avatar speech integration will be
 * added later.
 */

import { create } from 'zustand'

// Warning types matching proctoring events
export type WarningType = 'face' | 'multiple' | 'tab' | 'fullscreen' | 'general' | null

interface WarningState {
  // Current active warning
  activeWarning: string | null
  warningType: WarningType
  warningCount: number
  lastWarningTime: number

  // Actions
  warn: (message: string, type: WarningType, options?: { force?: boolean }) => void
  dismiss: () => void
  markResolved: () => void
  /** Clear throttle for a warning type so the next event can show immediately */
  clearTypeThrottle: (type: WarningType) => void
}

// Cooldown period between same-type warnings (10 seconds)
const WARNING_COOLDOWN_MS = 10000

// Auto-dismiss warnings after 8 seconds
const AUTO_DISMISS_MS = 8000

// Track auto-dismiss timers
let autoDismissTimer: ReturnType<typeof setTimeout> | null = null

// Track last warning time per type for throttling
const lastWarningByType: Map<WarningType, number> = new Map()

export const useWarningStore = create<WarningState>((set, get) => ({
  // Initial state
  activeWarning: null,
  warningType: null,
  warningCount: 0,
  lastWarningTime: 0,

  // Show a warning with throttling (unless force: true)
  warn: (message: string, type: WarningType, options?: { force?: boolean }) => {
    const now = Date.now()
    const lastTime = lastWarningByType.get(type) || 0

    // Throttle same-type warnings with 10s cooldown (skip when force)
    if (!options?.force && type && now - lastTime < WARNING_COOLDOWN_MS) {
      return
    }

    // Update last warning time for this type
    if (type) {
      lastWarningByType.set(type, now)
    }

    // Clear any existing auto-dismiss timer
    if (autoDismissTimer) {
      clearTimeout(autoDismissTimer)
      autoDismissTimer = null
    }

    // Update state
    set((state) => ({
      activeWarning: message,
      warningType: type,
      warningCount: state.warningCount + 1,
      lastWarningTime: now,
    }))

    // Set auto-dismiss timer (8 seconds)
    autoDismissTimer = setTimeout(() => {
      const state = get()
      // Only auto-dismiss if this is still the same warning
      if (state.lastWarningTime === now) {
        set({
          activeWarning: null,
          warningType: null,
        })
      }
      autoDismissTimer = null
    }, AUTO_DISMISS_MS)
  },

  // Manually dismiss the current warning
  dismiss: () => {
    if (autoDismissTimer) {
      clearTimeout(autoDismissTimer)
      autoDismissTimer = null
    }

    set({
      activeWarning: null,
      warningType: null,
    })
  },

  // Mark warning as resolved (user fixed the issue)
  markResolved: () => {
    if (autoDismissTimer) {
      clearTimeout(autoDismissTimer)
      autoDismissTimer = null
    }

    set({
      activeWarning: null,
      warningType: null,
    })
  },

  clearTypeThrottle: (type: WarningType) => {
    if (type) {
      lastWarningByType.delete(type)
    }
  },
}))

// Reset module state (for testing)
export function resetWarningStoreState() {
  // Clear timers
  if (autoDismissTimer) {
    clearTimeout(autoDismissTimer)
    autoDismissTimer = null
  }
  // Clear throttle tracking
  lastWarningByType.clear()
  // Reset Zustand store
  useWarningStore.setState({
    activeWarning: null,
    warningType: null,
    warningCount: 0,
    lastWarningTime: 0,
  })
}

// Export hook for convenience
export { useWarningStore as default }
